import { useState } from 'react';
import styled from 'styled-components';
import {
  DateString,
  dateStringInTimeZone,
  getDayOfMonth,
  getDayOfWeekFromDateString,
  getMonthName,
  getYear,
  mountainTimeZone,
} from '../../../../../common/src/date_string.ts';
import { Spinner } from '../../../util/spinner.tsx';
import { EngineeringAdminWrapper } from '../engineering/shared.tsx';
import { GitHubIssuesTable } from './github_issues_table.tsx';
import { HuddleOrgDashboardSection } from './huddle_org_dashboard.tsx';
import { PrayerFocusCard } from './prayer_focus_card.tsx';
import { useHuddle } from './use_huddle.ts';

// e.g. "Wednesday, June 18, 2025"
export function formatHuddleDate(date: DateString): string {
  return `${getDayOfWeekFromDateString(date)}, ${getMonthName(date)} ${getDayOfMonth(date)}, ${getYear(date)}`;
}

// e.g. "Monday 2:00 AM" - when the rotation next moves on (weekdays, see huddle_scheduler.ts)
export function formatNextHuddle(date: DateString): string {
  return `${getDayOfWeekFromDateString(date)} 2:00 AM`;
}

export const HuddlePage = () => {
  const { huddle, isLoading, error } = useHuddle();

  // the huddle day is Mountain's, not the browser's - before the response arrives, the header still
  // has to name a plausible day (read in a state initializer: the clock is impure).  Once the huddle
  // arrives the heading is its own date, which on the weekend is the preceding Friday.
  const [today] = useState(() => dateStringInTimeZone(new Date(), mountainTimeZone));
  const date = huddle?.date ?? today;

  const activeCard = huddle?.cards.find(({ orgClass }) => orgClass === 'active');
  const inactiveCard = huddle?.cards.find(({ orgClass }) => orgClass === 'inactive');

  return (
    <EngineeringAdminWrapper title={'Morning Huddle'}>
      <OuterWrapper>
        <DateHeading>{formatHuddleDate(date)}</DateHeading>
        {huddle && <NextHuddle>Next huddle {formatNextHuddle(huddle.nextHuddleDate)}</NextHuddle>}
        {isLoading && <Spinner />}
        {!isLoading && error && <Message>Unable to load the huddle: {error}</Message>}
        {!isLoading && !error && huddle && (
          <>
            {!huddle.date && <Message>No huddle yet — the rotation has not advanced for the first time.</Message>}
            {huddle.date && huddle.cards.length === 0 && <Message>No churches to pray for today.</Message>}
            <Cards>
              {activeCard && <PrayerFocusCard card={activeCard} />}
              {/* directly beneath the active card, and only that one: the dashboard is a celebration
                  of what a singing church looks like, not a diagnosis of a quiet one */}
              {activeCard && (
                <DashboardCell>
                  <HuddleOrgDashboardSection
                    orgId={activeCard.orgId}
                    orgName={activeCard.name}
                    orgType={activeCard.type}
                  />
                </DashboardCell>
              )}
              {inactiveCard && (
                <InactiveCell $besideActiveCard={!!activeCard}>
                  <PrayerFocusCard card={inactiveCard} />
                </InactiveCell>
              )}
            </Cards>
          </>
        )}
        {/* outside the card states on purpose - the two sections are independent in both directions */}
        <GitHubIssuesTable />
      </OuterWrapper>
    </EngineeringAdminWrapper>
  );
};

const OuterWrapper = styled.div`
  text-align: left;
  padding: 0 40px 40px;
`;

const DateHeading = styled.h2`
  text-align: left;
  margin: 0 0 4px;
  color: var(--color-text-darker);
  font-size: 1.1rem;
`;

const NextHuddle = styled.div`
  margin: 0 0 20px;
  color: var(--color-text-darker);
  font-size: 0.9rem;
`;

// The two cards sit side by side with the active church's dashboard spanning the row beneath them,
// so the dashboard reads as belonging to the card above it rather than to the pair.  Narrow screens
// collapse to one column, where the DOM order - active card, dashboard, inactive card - keeps them
// together.
const singleColumnWidth = '1000px';

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 480px));
  align-items: start;
  gap: 24px;
  @media (max-width: ${singleColumnWidth}) {
    grid-template-columns: minmax(0, 480px);
  }
`;

const DashboardCell = styled.div`
  grid-column: 1 / -1;
  @media (max-width: ${singleColumnWidth}) {
    grid-column: auto;
  }
`;

const InactiveCell = styled.div<{ $besideActiveCard: boolean }>`
  /* pinned to the top right only when there is an active card to sit beside; on its own it flows
     into the first cell rather than leaving a hole */
  ${(props) => (props.$besideActiveCard ? 'grid-column: 2; grid-row: 1;' : '')}
  @media (max-width: ${singleColumnWidth}) {
    grid-column: auto;
    grid-row: auto;
  }
`;

const Message = styled.div`
  color: var(--color-text-darker);
`;
